import { ICreatePartnerDTO, IPartnerProfile, IUpdatePartnerDTO, IPartnerBankAccountDTO } from '@/models/partners.model';
import { OwnershipTypes, PartnerFormFieldsNames, TPartnerFormValues } from '../helpers/types';
import { phoneSymbolsClearRegex } from '@/constants/regex';

class PartnerProfileDTO implements Partial<IUpdatePartnerDTO> {
  first_name: string;
  middle_name: string;
  last_name: string;
  title: string;
  email: string;
  phone: string;
  legal_type: 'fop' | 'tov';

  ownership_form?: OwnershipTypes;
  edrpou?: string;
  drfo?: string;
  is_vat_payer?: boolean;
  ipn?: string;
  tax_type?: 'simple' | 'general';

  legal_address?: string;
  physical_address?: string;
  subscriber_full_name?: string;

  bank_accounts?: IPartnerBankAccountDTO[];

  has_no_accountant?: boolean;
  accountant_first_name?: string;
  accountant_last_name?: string;
  accountant_middle_name?: string;
  accountant_email?: string;
  accountant_phone?: string;

  category_manager_id?: string;
  document?: string;

  constructor(values: TPartnerFormValues, original?: IPartnerProfile | null) {
    const company = values.companyDraft;
    const accountant = values.accountantDraft;

    this.first_name = values.first_name;
    this.middle_name = values.middle_name;
    this.last_name = values.last_name;
    this.title = values.title;
    this.email = values.email;
    this.phone = values.phone.toString().replace(phoneSymbolsClearRegex, '');
    this.legal_type = values.legal_type as 'fop' | 'tov';

    if (company?.ownership != null) {
      this.ownership_form = company.ownership;

      if (company.ownership === OwnershipTypes.LEGAL_ENTITY) {
        this.edrpou = values[PartnerFormFieldsNames.TAX_CODE]?.trim();
      } else {
        this.drfo = company.drfo?.trim();
      }
    }

    if (company?.vatPayer != null) {
      this.is_vat_payer = company.vatPayer;

      if (company.vatPayer) {
        this.ipn = values.ipn?.trim();
      }
    }

    if (values.tax_type) {
      this.tax_type = values.tax_type as 'simple' | 'general';
    }

    if (values.legal_address != null) {
      this.legal_address = values.legal_address;
    }

    if (values.physical_address != null) {
      this.physical_address = values.physical_address;
    }

    if (values.subscriber_full_name != null) {
      this.subscriber_full_name = values.subscriber_full_name;
    }

    const bankName = values.bank_name?.trim();
    const iban = values.iban?.trim().toUpperCase();
    const mfo = values.bankAccountsDraft?.primaryMfo?.trim();

    if (bankName && mfo && iban) {
      this.bank_accounts = [
        { bank_name: bankName, mfo, iban },
        ...(original?.bank_accounts?.slice(1).map((account) => ({
          bank_name: account.bank_name,
          mfo: account.mfo,
          iban: account.iban,
        })) ?? []),
      ];
    }

    if (accountant?.noAccountant != null) {
      this.has_no_accountant = accountant.noAccountant;
    }

    if (accountant?.noAccountant !== true) {
      const fields = {
        accountant_first_name: accountant?.firstName,
        accountant_last_name: accountant?.lastName,
        accountant_middle_name: accountant?.middleName,
        accountant_email: accountant?.email,
        accountant_phone: accountant?.phone,
      };

      Object.entries(fields).forEach(([key, value]) => {
        if (value?.trim()) {
          (this as unknown as Record<string, unknown>)[key] =
            key === 'accountant_phone' ? value.replace(phoneSymbolsClearRegex, '') : value.trim();
        }
      });
    }

    this.category_manager_id = values.category_manager_id ?? original?.category_manager?.id ?? undefined;

    if (values.document) {
      this.document = values.document;
    }
  }

  toCreateDTO(): ICreatePartnerDTO {
    if (
      !this.ownership_form ||
      this.is_vat_payer == null ||
      !this.tax_type ||
      !this.legal_address?.trim() ||
      !this.physical_address?.trim() ||
      !this.subscriber_full_name?.trim() ||
      !this.bank_accounts?.length ||
      this.has_no_accountant == null
    ) {
      throw new Error('Required partner requisites are missing');
    }

    return {
      ...this,
      ownership_form: this.ownership_form,
      is_vat_payer: this.is_vat_payer,
      tax_type: this.tax_type,
      legal_address: this.legal_address,
      physical_address: this.physical_address,
      subscriber_full_name: this.subscriber_full_name,
      bank_accounts: this.bank_accounts,
      has_no_accountant: this.has_no_accountant,
    };
  }
}

export default PartnerProfileDTO;
